import { RubyLoader } from '../components/RubyLoader';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ChevronLeft, Receipt, Printer, Mail, ExternalLink,
  RotateCcw, Undo2, CheckCircle2, XCircle, Clock, AlertCircle,
  Copy, Check, CreditCard, Cpu, MapPin, User, Package, Zap,
  ShieldCheck, Radio, Activity, FileText, Search,
} from 'lucide-react';
import { api } from '../services/apiService';
import RefundModal from '../components/payments/RefundModal';
import VoidModal from '../components/payments/VoidModal';

/**
 * Transaction detail — modern card-first layout.
 *
 * Design priorities (explicitly NOT mimicking NMI's dense portal UI):
 *   1. Hero summary — the "what happened / how much / on what card" answer
 *      is readable at a glance without scrolling or scanning.
 *   2. Context-aware primary action — one bold button for the most likely
 *      next step (Refund for approved, Retry for declined, etc.) instead of
 *      a row of equal-weight buttons.
 *   3. Visual timeline — lifecycle events as a vertical rail with colored
 *      dots + icons, easier to reason about than a table.
 *   4. Progressive disclosure — gateway raw payload / audit fields tuck
 *      behind a toggle so the common view stays clean.
 *   5. Copy-on-click for every identifier — ops people live on these.
 *
 * The header/back button reuses the super-admin dark chrome so the page
 * feels native to the rest of the admin.
 */
const PaymentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);
  const [showRaw, setShowRaw] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  const [showRefund, setShowRefund] = useState(false);
  const [showVoid, setShowVoid] = useState(false);

  // Deep-link support: accept ?tenantId=... so we can link directly from
  // tenant contexts without relying on a previously-set activeTenantId.
  // Mirror it into sessionStorage so the shared api helper picks it up.
  useEffect(() => {
    const urlTenantId = searchParams.get('tenantId');
    if (urlTenantId) sessionStorage.setItem('activeTenantId', urlTenantId);
  }, [searchParams]);

  const fetchDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await api.posPayments.timeline(id!);
      setData(resp);
    } catch (err: any) {
      setError(err?.message || 'Failed to load payment');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const meta = useMemo(() => data?.payment ? derive(data.payment) : null, [data]);

  if (loading) {
    return (
      <RubyLoader variant="page" className="min-h-[60vh]" />
    );
  }

  if (error || !data?.payment || !meta) {
    return (
      <div className="p-6">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-stone-500 hover:text-stone-700 text-sm mb-4">
          <ChevronLeft size={16} /> Back
        </button>
        <div className="bg-white rounded-2xl border border-stone-200/60 p-8 text-center text-stone-500">
          {error || 'Payment not found'}
        </div>
      </div>
    );
  }

  const p = data.payment;
  const { amount, subtotal, tip, surcharge, tone, status } = meta;

  return (
    <div className="min-h-screen bg-stone-100 -m-3 md:-m-6">
      {/* Header chrome */}
      <div className="bg-black px-4 py-4 md:py-5">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-gray-400 hover:text-white text-sm mb-3"
        >
          <ChevronLeft size={16} /> Back to Transactions
        </button>
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-gray-800 border border-gray-700 shrink-0">
            <Receipt size={18} className="text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-white text-base md:text-lg font-semibold truncate">Transaction</h1>
            <CopyChip label={p.transactionId} small dark />
          </div>
        </div>
      </div>

      <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-4">
        {/* ── HERO — the single glanceable card ──────────────────── */}
        <div className={`relative rounded-3xl shadow-lg overflow-hidden border ${tone.heroBorder}`}>
          <div className={`absolute inset-0 ${tone.heroGradient}`} />
          {status === 'sent_to_poi' && (
            <div className="absolute top-4 right-4 flex items-center gap-1.5 text-xs font-medium text-white/90 bg-white/10 backdrop-blur-sm px-2.5 py-1 rounded-full border border-white/20">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
              </span>
              Live
            </div>
          )}
          <div className="relative px-6 md:px-8 py-6 md:py-8">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="space-y-3 min-w-0 flex-1">
                <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide ${tone.pill}`}>
                  <tone.Icon size={13} className={tone.iconClass} />
                  {tone.label}
                </div>
                <div className="flex items-baseline gap-1 text-white">
                  <span className="text-xl md:text-2xl font-medium opacity-80">$</span>
                  <span className="text-5xl md:text-6xl font-bold leading-none tracking-tight">
                    {Math.floor(amount)}
                  </span>
                  <span className="text-2xl md:text-3xl font-bold leading-none opacity-90">
                    .{String(Math.round((amount - Math.floor(amount)) * 100)).padStart(2, '0')}
                  </span>
                  <span className="text-sm md:text-base font-medium text-white/70 ml-2">
                    {p.currency || 'USD'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs md:text-sm text-white/80">
                  <Clock size={13} />
                  {formatDate(p.createdAt)}
                </div>
              </div>

              {/* Card brand mini-card */}
              {(p.cardBrand || p.cardLast4 || p.last4) && (
                <div className={`${cardBrandStyle(p.cardBrand).bg} rounded-xl p-4 shadow-xl min-w-[200px] backdrop-blur-sm border border-white/10`}>
                  <div className="flex items-start justify-between">
                    <CreditCard size={20} className="text-white/90" />
                    <span className="text-[10px] font-bold text-white/80 uppercase tracking-widest">
                      {cardBrandStyle(p.cardBrand).label}
                    </span>
                  </div>
                  <div className="mt-5 font-mono text-white text-lg tracking-widest">
                    •••• {p.cardLast4 || p.last4 || '••••'}
                  </div>
                  {p.approvalCode && (
                    <div className="mt-3 text-[10px] text-white/70 uppercase tracking-wider">
                      Auth <span className="font-mono text-white/90">{p.approvalCode}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Primary action — context aware */}
            <div className="mt-6 flex flex-wrap gap-2">
              {meta.primaryAction && (
                <button
                  onClick={meta.primaryAction.onClick(setShowRefund, setShowVoid)}
                  disabled={meta.primaryAction.disabled}
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold shadow-sm ${meta.primaryAction.cls} disabled:opacity-40 disabled:cursor-not-allowed`}
                >
                  <meta.primaryAction.icon size={15} />
                  {meta.primaryAction.label}
                </button>
              )}
              <button className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium bg-white/10 hover:bg-white/15 text-white backdrop-blur-sm border border-white/20">
                <Printer size={14} /> Print receipt
              </button>
              <button className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium bg-white/10 hover:bg-white/15 text-white backdrop-blur-sm border border-white/20">
                <Mail size={14} /> Email
              </button>
            </div>
          </div>
        </div>

        {/* ── Quick-stat row ─────────────────────────────────────── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={Receipt} label="Subtotal" value={`$${subtotal.toFixed(2)}`} />
          <StatCard icon={Zap} label="Tip" value={`$${tip.toFixed(2)}`} accent={tip > 0 ? 'emerald' : undefined} />
          <StatCard icon={Activity} label="Surcharge" value={`$${surcharge.toFixed(2)}`} />
          <StatCard icon={CheckCircle2} label="Total Paid" value={`$${amount.toFixed(2)}`} accent="bold" />
        </div>

        {/* ── Details grid — 2-col on desktop, card-per-row mobile ─ */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Payment + terminal context */}
          <DetailCard title="Payment" icon={FileText}>
            <DetailRow label="Type" value={methodLabel(p.method)} />
            <DetailRow label="Entry" value={entryMethodLabel(p)} />
            <DetailRow label="Gateway" value={gatewayLabel(p.gatewayProvider)} />
            <DetailRow label="Response" value={p.responseCode || (status === 'completed' ? '100' : status === 'declined' ? '200' : '—')} mono />
            <DetailRow label="Auth Code" value={p.approvalCode || '—'} mono />
            {p.cardExp && <DetailRow label="Expires" value={p.cardExp} mono />}
          </DetailCard>

          <DetailCard title="Where" icon={MapPin}>
            <DetailRow label="Location" value={p.locationName} idHint={p.locationId} hideIfEmpty />
            <DetailRow label="Register" value={p.registerName} idHint={p.registerId} hideIfEmpty />
            <DetailRow label="Cashier" value={p.cashierName} idHint={p.employeeId} hideIfEmpty />
            <DetailRow label="EMV Device" value={p.emvDeviceName} idHint={p.emvDeviceId} hideIfEmpty />
            <DetailRow label="Tenant" value={p.tenantName} idHint={p.tenantId} hideIfEmpty />
          </DetailCard>
        </div>

        {/* ── Gateway telemetry card (webhook + settlement + ids) ─ */}
        {(p.webhookReceivedAt || p.asyncStatusGuid || p.gatewayTransactionId) && (
          <DetailCard title="Gateway Telemetry" icon={Radio}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <div>
                <DetailRow label="NMI Txn ID" value={p.gatewayTransactionId || '—'} mono copy />
                <DetailRow label="Async GUID" value={p.asyncStatusGuid || '—'} mono copy />
                <DetailRow label="Event Type" value={p.nmiEventType || '—'} mono />
                <DetailRow label="Event ID" value={p.nmiLastEventId || '—'} mono copy />
              </div>
              <div>
                <DetailRow label="Webhook Received" value={p.webhookReceivedAt ? formatDate(p.webhookReceivedAt) : 'Not yet'} />
                <DetailRow label="Poll Resolved" value={p.pollResolvedAt ? formatDate(new Date(p.pollResolvedAt).toISOString()) : '—'} />
                <DetailRow label="Poll Iterations" value={p.pollIterations != null ? String(p.pollIterations) : '—'} />
                <DetailRow label="Settlement Batch" value={p.settlementBatchId || 'Unsettled'} mono copy />
                <DetailRow label="Settled At" value={p.settledAt ? formatDate(new Date(p.settledAt).toISOString()) : '—'} />
              </div>
            </div>
            {p.webhookReceivedAt && (
              <div className="mt-4 pt-4 border-t border-stone-200/60 flex flex-wrap items-center gap-4 text-xs">
                <Badge color="emerald" icon={ShieldCheck} label="Signature verified" />
                <Badge color="emerald" icon={Radio} label="Pushed to POS" />
              </div>
            )}
          </DetailCard>
        )}

        {/* ── Lifecycle timeline — vertical rail ─────────────────── */}
        {(data.events || []).length > 0 && (
          <DetailCard title="Lifecycle" icon={Activity}>
            <div className="relative pl-6">
              <div className="absolute left-[7px] top-2 bottom-2 w-px bg-stone-200" />
              {(data.events || []).map((ev: any, i: number) => {
                const classify = classifyEvent(ev.title);
                return (
                  <div key={i} className="relative pb-4 last:pb-0">
                    <div className={`absolute -left-6 top-1 w-3.5 h-3.5 rounded-full border-2 border-white ring-2 ${classify.ring} ${classify.dot}`} />
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-stone-800 flex items-center gap-1.5">
                          <classify.Icon size={13} className={classify.iconColor} />
                          {ev.title}
                        </p>
                        {ev.detail && (
                          <p className="text-xs text-stone-500 mt-0.5 font-mono truncate">{ev.detail}</p>
                        )}
                        <p className="text-[11px] text-stone-400 mt-0.5">{formatDate(ev.at)}</p>
                      </div>
                      {ev.amount && (
                        <span className="text-sm font-semibold text-stone-800 whitespace-nowrap">
                          ${Number(ev.amount).toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </DetailCard>
        )}

        {/* ── Related lookups ────────────────────────────────────── */}
        {(p.cardLast4 || p.last4) && (
          <button
            className="w-full bg-white border border-stone-200/60 rounded-2xl px-5 py-3 text-sm text-blue-600 hover:bg-blue-50 inline-flex items-center justify-center gap-2 font-medium transition-colors"
            onClick={() => navigate(`/pos/payments?search=${p.cardLast4 || p.last4}`)}
          >
            <Search size={14} />
            Find other transactions on card ••••{p.cardLast4 || p.last4}
          </button>
        )}

        {/* ── Progressive disclosure: raw + audit ────────────────── */}
        <div className="space-y-2">
          {p.nmiResponse && (
            <div className="bg-white rounded-2xl border border-stone-200/60 overflow-hidden">
              <button
                onClick={() => setShowRaw(v => !v)}
                className="w-full flex items-center justify-between px-5 py-3 text-sm font-medium text-stone-700 hover:bg-stone-50"
              >
                <span className="inline-flex items-center gap-2">
                  <ExternalLink size={14} className="text-stone-400" />
                  NMI raw payload
                </span>
                <span className="text-xs text-stone-400">{showRaw ? 'Hide' : 'Show'}</span>
              </button>
              {showRaw && (
                <pre className="mx-5 mb-4 bg-stone-900 text-emerald-300 text-[11px] rounded-lg p-3 overflow-x-auto font-mono max-h-96">
                  {JSON.stringify(p.nmiResponse, null, 2)}
                </pre>
              )}
            </div>
          )}

          <div className="bg-white rounded-2xl border border-stone-200/60 overflow-hidden">
            <button
              onClick={() => setShowAudit(v => !v)}
              className="w-full flex items-center justify-between px-5 py-3 text-sm font-medium text-stone-700 hover:bg-stone-50"
            >
              <span className="inline-flex items-center gap-2">
                <FileText size={14} className="text-stone-400" />
                Audit fields
              </span>
              <span className="text-xs text-stone-400">{showAudit ? 'Hide' : 'Show'}</span>
            </button>
            {showAudit && (
              <div className="mx-5 mb-4 text-xs space-y-1 font-mono text-stone-500 bg-stone-50 rounded-lg p-3">
                <div>createdAt: {p.createdAt}</div>
                <div>updatedAt: {p.updatedAt}</div>
                <div>idempotencyKey: {p.idempotencyKey || '—'}</div>
                <div>orderId: {p.orderId || '—'}</div>
                <div>status: {p.status}</div>
                <div>normalisedMethod: {p.normalisedMethod || '—'}</div>
              </div>
            )}
          </div>
        </div>

        <div className="h-6" />
      </div>

      {showRefund && (
        <RefundModal
          payment={{
            transactionId: p.transactionId,
            amount: amount,
            refundedAmount: p.refundedAmount || 0,
          }}
          onClose={() => setShowRefund(false)}
          onRefunded={async () => { setShowRefund(false); await fetchDetail(); }}
        />
      )}
      {showVoid && (
        <VoidModal
          payment={{ transactionId: p.transactionId, amount: amount }}
          onClose={() => setShowVoid(false)}
          onVoided={async () => { setShowVoid(false); await fetchDetail(); }}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

interface DetailCardProps { title: string; icon: React.FC<any>; children: React.ReactNode }
const DetailCard: React.FC<DetailCardProps> = ({ title, icon: Icon, children }) => (
  <div className="bg-white rounded-2xl border border-stone-200/60 overflow-hidden">
    <div className="px-5 py-3 border-b border-stone-200/60 flex items-center gap-2">
      <Icon size={14} className="text-stone-400" />
      <h3 className="text-stone-800 font-semibold text-sm">{title}</h3>
    </div>
    <div className="px-5 py-3">{children}</div>
  </div>
);

interface DetailRowProps { label: string; value: string; mono?: boolean; copy?: boolean; idHint?: string; hideIfEmpty?: boolean }
const DetailRow: React.FC<DetailRowProps> = ({ label, value, mono, copy, idHint, hideIfEmpty }) => {
  // Hide entirely when the value is missing/dash — admin users don't want to
  // see opaque ids when no name resolved. The id stays accessible via the
  // copy chip on rows where we do have a name.
  const isEmpty = !value || value === '—';
  if (isEmpty && hideIfEmpty) return null;
  return (
    <div className="flex items-center justify-between gap-4 py-1.5 text-sm group">
      <span className="text-stone-500 text-xs uppercase tracking-wide">{label}</span>
      <span className={`text-stone-800 font-medium truncate ${mono ? 'font-mono text-[13px]' : ''} flex items-center gap-2`}>
        <span className="truncate">{value}</span>
        {copy && !isEmpty && <CopyBtn text={value} />}
        {idHint && !isEmpty && idHint !== value && <CopyBtn text={idHint} />}
      </span>
    </div>
  );
};

const CopyBtn: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      }}
      className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-stone-100 transition-opacity"
      title="Copy"
    >
      {copied ? <Check size={11} className="text-emerald-500" /> : <Copy size={11} className="text-stone-400" />}
    </button>
  );
};

const CopyChip: React.FC<{ label: string; small?: boolean; dark?: boolean }> = ({ label, small, dark }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(label); setCopied(true); setTimeout(() => setCopied(false), 1200); }}
      className={`inline-flex items-center gap-1.5 ${small ? 'text-xs' : 'text-sm'} ${dark ? 'text-gray-400 hover:text-white' : 'text-stone-500 hover:text-stone-700'} font-mono transition-colors`}
    >
      {label}
      {copied ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />}
    </button>
  );
};

interface StatCardProps { icon: React.FC<any>; label: string; value: string; accent?: 'emerald' | 'bold' }
const StatCard: React.FC<StatCardProps> = ({ icon: Icon, label, value, accent }) => (
  <div className="bg-white rounded-xl border border-stone-200/60 p-3">
    <div className="flex items-center gap-1.5 text-[11px] text-stone-500 uppercase tracking-wide">
      <Icon size={11} /> {label}
    </div>
    <div className={`mt-1 text-lg font-bold ${accent === 'emerald' ? 'text-emerald-700' : accent === 'bold' ? 'text-stone-900 text-xl' : 'text-stone-800'}`}>
      {value}
    </div>
  </div>
);

const Badge: React.FC<{ color: 'emerald' | 'red' | 'amber'; icon: React.FC<any>; label: string }> = ({ color, icon: Icon, label }) => {
  const map = {
    emerald: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    red: 'text-red-700 bg-red-50 border-red-200',
    amber: 'text-amber-700 bg-amber-50 border-amber-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold border ${map[color]}`}>
      <Icon size={11} /> {label}
    </span>
  );
};

// ---------------------------------------------------------------------------
// Meta / style derivation
// ---------------------------------------------------------------------------

function derive(p: any) {
  const amounts = p.amounts || { subtotal: p.amount, tip: 0, surcharge: 0, total: p.amount };
  const amount = Number(amounts.total || p.amount || 0);
  const subtotal = Number(amounts.subtotal || 0);
  const tip = Number(amounts.tip || 0);
  const surcharge = Number(amounts.surcharge || 0);
  const status = p.status;

  const tone = statusTone(status);
  const primaryAction = chooseAction(status, p);

  return { amount, subtotal, tip, surcharge, tone, status, primaryAction };
}

function statusTone(status: string): {
  label: string; pill: string; Icon: React.FC<any>; iconClass: string;
  heroGradient: string; heroBorder: string;
} {
  const approved = ['completed', 'approved', 'settled'].includes(status);
  const pending = ['sent_to_poi', 'pending'].includes(status);
  const failed = ['declined', 'cancelled', 'error', 'timeout'].includes(status);
  const voided = ['voided', 'refunded', 'partially_refunded', 'orphaned', 'disputed'].includes(status);

  if (approved) return {
    label: status === 'settled' ? 'Settled' : 'Approved',
    pill: 'bg-emerald-500/20 text-emerald-100 border border-emerald-400/30',
    Icon: CheckCircle2, iconClass: 'text-emerald-200',
    heroGradient: 'bg-gradient-to-br from-emerald-600 via-emerald-700 to-emerald-800',
    heroBorder: 'border-emerald-900/20',
  };
  if (pending) return {
    label: 'Awaiting card',
    pill: 'bg-blue-500/20 text-blue-100 border border-blue-400/30',
    Icon: Clock, iconClass: 'text-blue-200',
    heroGradient: 'bg-gradient-to-br from-blue-600 via-blue-700 to-rose-800',
    heroBorder: 'border-blue-900/20',
  };
  if (failed) return {
    label: status === 'cancelled' ? 'Cancelled' : status === 'timeout' ? 'Timed out' : 'Declined',
    pill: 'bg-red-500/20 text-red-100 border border-red-400/30',
    Icon: XCircle, iconClass: 'text-red-200',
    heroGradient: 'bg-gradient-to-br from-red-600 via-red-700 to-red-900',
    heroBorder: 'border-red-900/20',
  };
  if (voided) return {
    label: status === 'voided' ? 'Voided' : status === 'refunded' ? 'Refunded' : status === 'disputed' ? 'Chargeback' : 'Partial refund',
    pill: 'bg-amber-500/20 text-amber-100 border border-amber-400/30',
    Icon: AlertCircle, iconClass: 'text-amber-200',
    heroGradient: 'bg-gradient-to-br from-amber-600 via-orange-700 to-amber-800',
    heroBorder: 'border-amber-900/20',
  };
  return {
    label: status || 'Unknown',
    pill: 'bg-stone-500/20 text-stone-100 border border-stone-400/30',
    Icon: Clock, iconClass: 'text-stone-200',
    heroGradient: 'bg-gradient-to-br from-stone-600 via-stone-700 to-stone-800',
    heroBorder: 'border-stone-900/20',
  };
}

function chooseAction(status: string, p: any): {
  label: string; icon: React.FC<any>; cls: string;
  disabled?: boolean;
  onClick: (setRefund: (v: boolean) => void, setVoid: (v: boolean) => void) => () => void;
} | null {
  const amount = Number(p.amounts?.total || p.amount || 0);
  const refunded = Number(p.refundedAmount || 0);

  if (status === 'completed' || status === 'approved' || status === 'settled') {
    const canVoid = !p.batchId && status !== 'settled';
    if (canVoid) {
      return {
        label: `Void this transaction`,
        icon: Undo2,
        cls: 'bg-white text-red-600 hover:bg-white/90',
        onClick: (_setRefund, setVoid) => () => setVoid(true),
      };
    }
    if (refunded < amount) {
      return {
        label: `Refund $${(amount - refunded).toFixed(2)}`,
        icon: RotateCcw,
        cls: 'bg-white text-stone-800 hover:bg-white/90',
        onClick: (setRefund) => () => setRefund(true),
      };
    }
  }
  return null;
}

function classifyEvent(title: string): { Icon: React.FC<any>; dot: string; ring: string; iconColor: string } {
  const t = (title || '').toLowerCase();
  if (/declin|fail|cancel|error/.test(t)) return { Icon: XCircle, dot: 'bg-red-500', ring: 'ring-red-100', iconColor: 'text-red-500' };
  if (/approv|complete|success|settle/.test(t)) return { Icon: CheckCircle2, dot: 'bg-emerald-500', ring: 'ring-emerald-100', iconColor: 'text-emerald-500' };
  if (/webhook|poll/.test(t)) return { Icon: Radio, dot: 'bg-blue-500', ring: 'ring-blue-100', iconColor: 'text-blue-500' };
  if (/sent|init|start/.test(t)) return { Icon: Zap, dot: 'bg-rose-500', ring: 'ring-rose-100', iconColor: 'text-rose-500' };
  return { Icon: Clock, dot: 'bg-stone-400', ring: 'ring-stone-100', iconColor: 'text-stone-500' };
}

function cardBrandStyle(brand: string): { bg: string; label: string } {
  const b = (brand || '').toLowerCase();
  if (b === 'visa') return { bg: 'bg-gradient-to-br from-blue-600 to-blue-800', label: 'Visa' };
  if (b === 'mastercard' || b === 'mc') return { bg: 'bg-gradient-to-br from-orange-500 to-red-600', label: 'Mastercard' };
  if (b === 'amex' || b === 'american express') return { bg: 'bg-gradient-to-br from-sky-600 to-blue-800', label: 'AmEx' };
  if (b === 'discover') return { bg: 'bg-gradient-to-br from-orange-400 to-orange-600', label: 'Discover' };
  if (b === 'diners') return { bg: 'bg-gradient-to-br from-stone-600 to-stone-800', label: 'Diners' };
  return { bg: 'bg-gradient-to-br from-stone-700 to-stone-900', label: brand?.toUpperCase() || 'CARD' };
}

function methodLabel(m: string): string {
  if (!m) return '—';
  if (m === 'credit_nmi_cloud' || m === 'card_emv' || m === 'external_card') return 'Card sale';
  if (m === 'cash') return 'Cash';
  if (m === 'ebt') return 'EBT';
  return m.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function gatewayLabel(g: string): string {
  if (!g) return '—';
  if (g === 'nmi_cloud') return 'NMI Cloud';
  if (g === 'manual_external') return 'External terminal';
  if (g === 'cash') return 'Cash drawer';
  return g;
}

function entryMethodLabel(p: any): string {
  const entry = p?.nmiResponse?.event_body?.card?.entry_mode || p.cardEntryMethod;
  if (!entry) return p.gatewayProvider === 'nmi_cloud' ? 'EMV Contactless' : '—';
  const e = String(entry).toLowerCase();
  if (e.includes('contactless') || e === 'tap') return 'Contactless';
  if (e === 'chip' || e.includes('emv')) return 'EMV Chip';
  if (e.includes('swipe') || e.includes('magstripe')) return 'Swiped';
  if (e.includes('keyed') || e.includes('manual')) return 'Keyed';
  return String(entry);
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch { return iso; }
}

export default PaymentDetailPage;
